import { getMetrikaCounterIdFromEnv } from "./metrikaCounterId";
import { isValidMetrikaNumericCid, readYmUidCookie, SITE_METRIKA_CLIENT_ID_KEY, } from "./siteAnalyticsStorage";
const METRIKA_INIT = {
    ssr: true,
    webvisor: true,
    clickmap: true,
    ecommerce: "dataLayer",
    accurateTrackBounce: true,
    trackLinks: true,
};
/**
 * Сколько ждём getClientID. Было 5000 мс на КАЖДЫЙ вызов (фолбэк не
 * кэшировался), и у пользователя с блокировщиком каждый вход/регистрация/OAuth
 * висел по 5 с. Эталоны: VPN — 1000 мс, Worldpay — 1500 мс.
 */
export const YM_CID_TIMEOUT_MS = 1500;
/**
 * Неудачу getClientID помним на сессию вкладки (с TTL): пока отметка свежая,
 * ClientID отдаём сразу из того, что уже есть (cookie `_ym_uid`), не дожидаясь
 * тега. Поздний ответ тега (догрузился после таймаута) всё равно подхватится —
 * см. requestYmClientId.
 */
export const YM_CID_FAILED_KEY = "virtuoz_ym_cid_failed_at";
export const YM_CID_FAILED_TTL_MS = 30 * 60 * 1000;
// Реальный ym ClientID (из getClientID), полученный на этой загрузке.
let resolvedRealCid = null;
// Единственный запрос getClientID на загрузку. Живёт и после таймаута
// ожидающих: если tag.js догрузится позже, ответ всё равно попадёт в кэш.
let cidRequest = null;
// Неудача уже случилась на ЭТОЙ загрузке (дублирует sessionStorage на случай,
// если он недоступен).
let cidFailedThisLoad = false;
function getProxyUrlFromEnv() {
    // Базовый URL прокси Метрики (свой домен). Подставляется webpack
    // DefinePlugin строковым литералом — см. metrikaCounterId.ts.
    const raw = process.env.REACT_APP_YM_PROXY_URL;
    if (!raw || typeof raw !== "string") {
        return null;
    }
    const t = raw.trim().replace(/\/+$/, "");
    return t || null;
}
/**
 * Очередь вызовов ym до загрузки tag.js (совместимо с официальным сниппетом).
 */
function installMetrikaStub() {
    if (typeof window === "undefined") {
        return;
    }
    if (window.ym) {
        return;
    }
    new Function("w", "w.ym=w.ym||function(){(w.ym.a=w.ym.a||[]).push(arguments)};w.ym.l=1*new Date()")(window);
}
function appendScript(src) {
    return new Promise((resolve, reject) => {
        const s = document.createElement("script");
        s.async = true;
        s.src = src;
        s.onload = () => resolve();
        s.onerror = () => {
            s.remove();
            reject(new Error(`script load failed: ${src}`));
        };
        document.head.appendChild(s);
    });
}
/**
 * tag.js уже подключён синхронным сниппетом из <head>. Тогда повторно
 * грузить скрипт не нужно — иначе будет дубликат тега.
 */
function isMetrikaTagScriptPresent() {
    if (typeof document === "undefined") {
        return false;
    }
    return !!document.querySelector('script[src*="/metrika/tag.js"]');
}
/**
 * Грузит tag.js с прокси-домена. Обычно синхронный сниппет в <head> уже
 * вставил его — тогда выходим сразу: нужен лишь стаб очереди ym (на случай,
 * если скрипт ещё грузится), а getClientID отработает, как только tag.js
 * догрузится.
 */
async function loadMetrikaTag(proxyUrl, counterId) {
    installMetrikaStub();
    if (isMetrikaTagScriptPresent()) {
        return;
    }
    const q = encodeURIComponent(counterId);
    await appendScript(`${proxyUrl}/metrika/tag.js?id=${q}`);
}
function initMetrikaOnce(counterId) {
    if (!window.ym) {
        return;
    }
    if (window.__VIRTUOZ_METRIKA_INIT_COUNTER === counterId) {
        return;
    }
    window.ym(counterId, "init", { ...METRIKA_INIT });
    window.__VIRTUOZ_METRIKA_INIT_COUNTER = counterId;
}
/**
 * getClientID БЕЗ таймаута: промис резолвится, когда тег ответил, или сразу —
 * если тега нет вовсе. Ожидание с дедлайном — у вызывающего
 * (resolveSiteMetrikaClientId), чтобы поздний ответ не пропадал.
 */
function readYmClientId(counterId) {
    return new Promise((resolve) => {
        if (!window.ym) {
            resolve({ cid: null, reason: "window.ym отсутствует (tag.js не загрузился)" });
            return;
        }
        try {
            window.ym(counterId, "getClientID", (cid) => {
                const t = typeof cid === "string" ? cid.trim() : "";
                if (t && isValidMetrikaNumericCid(t)) {
                    resolve({ cid: t, reason: null });
                }
                else {
                    resolve({
                        cid: null,
                        reason: `getClientID вернул невалидный id: ${JSON.stringify(cid)}`,
                    });
                }
            });
        }
        catch (e) {
            resolve({ cid: null, reason: `getClientID бросил исключение: ${String(e)}` });
        }
    });
}
function logResolvedCid(cid, source) {
    // eslint-disable-next-line no-console
    console.info(`[metrika] ycid=${cid} (источник: ${source})`);
}
function logYmCidFallbackReason(reason) {
    // eslint-disable-next-line no-console
    console.warn(`[metrika] ym getClientID не получен — ${reason}`);
}
function isCidFailureFresh(now) {
    if (cidFailedThisLoad) {
        return true;
    }
    try {
        const at = Number(sessionStorage.getItem(YM_CID_FAILED_KEY));
        return Number.isFinite(at) && at > 0 && now - at >= 0 && now - at < YM_CID_FAILED_TTL_MS;
    }
    catch {
        return false;
    }
}
function rememberCidFailure(now) {
    cidFailedThisLoad = true;
    try {
        sessionStorage.setItem(YM_CID_FAILED_KEY, String(now));
    }
    catch {
        /* ignore */
    }
}
function forgetCidFailure() {
    cidFailedThisLoad = false;
    try {
        sessionStorage.removeItem(YM_CID_FAILED_KEY);
    }
    catch {
        /* ignore */
    }
}
/**
 * Раньше при недоступном теге сюда писался синтетический 20-значный ClientID и
 * уходил на бэк как настоящий (фантомные посетители в MP, риск R4 из
 * metrika_comparison.md). Синтетику больше не генерируем; старое значение
 * чистим, чтобы его никто не принял за реальный ClientID.
 */
function dropLegacySyntheticCid() {
    try {
        localStorage.removeItem(SITE_METRIKA_CLIENT_ID_KEY);
    }
    catch {
        /* ignore */
    }
}
/** Один запрос getClientID на загрузку страницы (идемпотентно). */
function requestYmClientId() {
    if (cidRequest) {
        return cidRequest;
    }
    dropLegacySyntheticCid();
    cidRequest = (async () => {
        const counterId = getMetrikaCounterIdFromEnv();
        const proxyUrl = getProxyUrlFromEnv();
        if (counterId === null) {
            logYmCidFallbackReason("не задан REACT_APP_YM_COUNTER_ID");
            return null;
        }
        if (proxyUrl === null) {
            logYmCidFallbackReason("не задан REACT_APP_YM_PROXY_URL");
            return null;
        }
        try {
            await loadMetrikaTag(proxyUrl, String(counterId));
            initMetrikaOnce(counterId);
        }
        catch (e) {
            logYmCidFallbackReason(`tag.js не загрузился с прокси ${proxyUrl}: ${String(e)}`);
            return null;
        }
        const { cid, reason } = await readYmClientId(counterId);
        if (!cid) {
            logYmCidFallbackReason(reason || "getClientID не вернул id");
            return null;
        }
        resolvedRealCid = cid;
        forgetCidFailure();
        logResolvedCid(cid, "ym getClientID");
        return cid;
    })();
    return cidRequest;
}
/**
 * ClientID, который уже известен, — БЕЗ ожидания: реальный из getClientID
 * этой загрузки либо cookie `_ym_uid` (её ставит сам tag.js; значение
 * совпадает с getClientID). Нет — null. Для мест, которые нельзя задерживать
 * (создание платежа по клику).
 */
export function peekSiteMetrikaClientId() {
    return resolvedRealCid ?? readYmUidCookie();
}
/**
 * ClientID (ycid) для бэка. Порядок: ym getClientID (ждём не дольше
 * YM_CID_TIMEOUT_MS) → cookie `_ym_uid` → null. Синтетики нет: без ClientID
 * атрибуцию закрывает офлайн-конверсия по yclid (как в VPN/Worldpay).
 *
 * Не дождались — неудача запоминается (см. YM_CID_FAILED_TTL_MS), и следующие
 * вызовы отвечают сразу, без повторных зависаний на каждом auth-запросе.
 * Никогда не reject'ится.
 */
export async function resolveSiteMetrikaClientId() {
    if (resolvedRealCid) {
        return resolvedRealCid;
    }
    if (typeof window === "undefined") {
        return null;
    }
    const request = requestYmClientId();
    const now = Date.now();
    if (isCidFailureFresh(now)) {
        return peekSiteMetrikaClientId();
    }
    let timer;
    const cid = await Promise.race([
        request,
        new Promise((resolve) => {
            timer = window.setTimeout(() => resolve(null), YM_CID_TIMEOUT_MS);
        }),
    ]);
    window.clearTimeout(timer);
    if (cid) {
        return cid;
    }
    rememberCidFailure(Date.now());
    const fallback = readYmUidCookie();
    if (fallback) {
        logResolvedCid(fallback, "cookie _ym_uid");
    }
    return fallback;
}
